import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import strings from '../../constant/strings';
import { activationLoginAction } from '../../redux/auth/authActions';
import { setReturnTo } from '../../utils/returnTo';
import { clearDraft, clearIntent } from './utils';
import { CenterState, Muted, OutlineButton, Page } from './styles';

/**
 * Where the activation email lands.
 *
 * The patient has just confirmed their address and chosen a password minutes
 * ago, so asking them to type it again is a step that earns nothing. The
 * backend hands this page a one-time code; it trades that for a real session
 * and the normal post-login routing carries them on to their pre-order.
 *
 * Nothing is stored from the URL: the code is spent once, server-side, and the
 * tokens come back in the response body like any other login.
 */
const PreorderActivated = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { slug } = useParams();
  const { token, error } = useSelector((state) => ({
    token: state.auth.token,
    error: state.auth.error,
  }));

  const [failed, setFailed] = useState(false);
  // React 18 mounts effects twice in development; the code is single-use, so
  // spending it twice would fail the second attempt and look like an error.
  const exchanged = useRef(false);

  useEffect(() => {
    const code = new URLSearchParams(location.search).get('code');
    if (!code) {
      setFailed(true);
      return;
    }
    if (exchanged.current) return;
    exchanged.current = true;

    // Where to go once signed in. The login saga reads this, so the patient
    // lands where the server says rather than on the dashboard: on their
    // pre-orders when the order was placed from their saved details, or back
    // on the checkout when there was nothing to place (or placing it failed).
    const next = new URLSearchParams(location.search).get('next');
    setReturnTo(next === 'orders' ? '/patient/preorders' : `/preorder/${slug}`);
    // The server placed the order from its own copy, so the browser's is spent.
    clearDraft();
    clearIntent();
    dispatch(activationLoginAction({ code }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The saga routes on success; this only has to cover the failure.
  useEffect(() => {
    if (exchanged.current && !token && error) setFailed(true);
  }, [token, error]);

  if (failed) {
    return (
      <Page>
        <CenterState>
          <div style={{ fontWeight: 600, color: '#2B2B2B', marginBottom: 8 }}>
            {strings.activationLinkExpired}
          </div>
          <Muted style={{ marginBottom: 20 }}>{strings.activationLinkExpiredNote}</Muted>
          <OutlineButton onClick={() => history.replace(`/preorder/${slug}/login?activated=1`)}>
            {strings.signInNow}
          </OutlineButton>
        </CenterState>
      </Page>
    );
  }

  return (
    <Page>
      <CenterState>
        <div style={{ fontWeight: 600, color: '#15795A', marginBottom: 8 }}>
          {strings.accountActivated}
        </div>
        <Muted>{strings.signingYouIn}</Muted>
      </CenterState>
    </Page>
  );
};

export default PreorderActivated;
